import { useEffect, useCallback, useRef, useState } from 'react';
import * as Sentry from '@sentry/browser';

import { open, close, postMessageToWindow } from '../utils/newWindowUtils';
import { getUrlWithHttps } from '../utils/getUrlWithHttps';
import { MANUAL_RETAKER_CS_MESSAGES } from '../constants-types/ClientScriptMessages';
import { HEATMAP_WIDTH_PER_DEVICE } from '../constants-types/HeatmapWidthPerDevice';
import type { ManualRetakerEvent, RetakerMode } from '../constants-types/ManualRetakerTypes';

interface UseManualRetakerProps {
  handleManualRetakerClick: (url: string, mode?: RetakerMode) => void;
  isManualRetakerUrlModalOpen: boolean;
  handleManualRetakerUrlModalClose: () => void;
  handleManualRetakerUrlSubmit: (url: string) => void;
}

const WINDOW_HEIGHT_MARGIN = 150;

const getBaseUrl = (url: string): string => {
  const baseUrl = new URL(url);
  return `${baseUrl.protocol}//${baseUrl.hostname}`;
};

export function useManualRetaker(
  device: HeatmapDeviceTypes,
  onSuccess: (pageContent: string, triggerSource: RetakerTriggerSource) => void,
): UseManualRetakerProps {
  const [isManualRetakerUrlModalOpen, setManualRetakerUrlModalOpen] = useState(false);

  const originRef = useRef<string>('');
  const retakerMode = useRef<RetakerMode | undefined>(undefined);

  const openManualRetaker = (url: string) => {
    if (!url) return;

    open(
      getUrlWithHttps(url),
      '_blank',
      `width=${HEATMAP_WIDTH_PER_DEVICE[device]},height=${
        window.screen.availHeight - WINDOW_HEIGHT_MARGIN
      }`,
    );
  };

  const handleManualRetakerUrlModalClose = () => {
    setManualRetakerUrlModalOpen(false);
  };

  const handleManualRetakerUrlSubmit = (url: string) => {
    openManualRetaker(url);
    setManualRetakerUrlModalOpen(false);
  };

  const handleManualRetakerClick = (url: string, mode?: RetakerMode) => {
    retakerMode.current = mode;
    if (!url) {
      setManualRetakerUrlModalOpen(true);
    } else {
      openManualRetaker(url);
    }
  };

  const handleClientMessage = useCallback(
    (event: MessageEvent<ManualRetakerEvent>) => {
      if (
        event?.data?.type === MANUAL_RETAKER_CS_MESSAGES.READY &&
        event.data.origin !== undefined &&
        Boolean(event.data.origin)
      ) {
        // We return early and don't send back the start message if the previously-stored
        // origin doesn't match the origin sent in the new message (security considerations).
        // This is relevant for redirection or manual navigation in the retaker window.
        if (Boolean(originRef.current) && event.data.origin !== originRef.current) return;

        originRef.current = event.data.origin;
        postMessageToWindow(
          MANUAL_RETAKER_CS_MESSAGES.START,
          event.data.origin,
          retakerMode.current,
        );
      }

      if (
        event?.data?.type === MANUAL_RETAKER_CS_MESSAGES.SEND_HTML &&
        getBaseUrl(event.origin) === getBaseUrl(originRef.current) &&
        event?.data?.payload
      ) {
        onSuccess(event.data.payload.html, event.data.payload.trigger);
        close();
      } else {
        Sentry.captureMessage(
          `Error retaking screenshot of ${event.origin}, originRef: ${originRef.current}`,
          Sentry.Severity.Warning,
        );
      }
    },
    [onSuccess],
  );

  useEffect(() => {
    window.addEventListener('message', handleClientMessage);

    return () => {
      window.removeEventListener('message', handleClientMessage);
    };
  }, [handleClientMessage]);

  // Clean up - automatically close the window if the hook is gonna be unmounted.
  // e.g. User performs a new search, changes report type, or navigates away from heatmaps.
  useEffect(() => {
    return () => {
      close();
    };
  }, []);

  return {
    handleManualRetakerClick,
    isManualRetakerUrlModalOpen,
    handleManualRetakerUrlSubmit,
    handleManualRetakerUrlModalClose,
  };
}
